import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withFormik } from 'formik';
import { withRouter, Link } from 'react-router-dom';
import { compose, defaultTo, path, find, filter, propEq, isEmpty, sum } from 'ramda';
import Select from 'react-select';
import moment from 'moment';

import magic from '../../../../util/magic';
import { routes } from '../../../../routes';
import { handleError, handleSuccess } from '../../../../util/forms';
import { apiAddFile, apiGetExperiencesFromRegistration } from '../../../../api/experience';

import Card from '../../../../components/Card';
import FlaggedUserModal from '../../../../components/Modal/FlaggedUserModal';
import EditField from '../../../../components/Forms/EditField';
import FileBrowser from '../../../../components/Forms/FileBrowser';
import { isNilOrEmpty } from '../../../../util/redux';
import File from '../../../../components/File';

class Experience extends Component {
  static propTypes = {
    admin: PropTypes.bool,
    approve: PropTypes.func,
    reject: PropTypes.func,
    experience: PropTypes.object,
    fellowshipServiceTypes: PropTypes.array,
    registrations: PropTypes.array,
    finish: PropTypes.func,
    history: PropTypes.object,
    submit: PropTypes.func,
  };

  static defaultProps = {
    experience: {},
  };

  state = {
    experiences: [],
  };

  componentDidMount() {
    if (this.props.values.registrationId) this.updateExperiences();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.values.registrationId != this.props.values.registrationId && this.props.values.registrationId) {
      this.updateExperiences();
    }
    if (prevProps.values.fellowshipServiceTypeId != this.props.values.fellowshipServiceTypeId) {
      this.updateHoursLeft();
    }
  }

  updateExperiences = () => {
    apiGetExperiencesFromRegistration({ registrationId: this.props.values.registrationId }).then(({ data }) => {
      this.setState({ experiences: defaultTo([], data) });
      this.updateHoursLeft();
    });
  };

  // Find the maximum hours left for the selected service type
  updateHoursLeft = () => {
    const { values, experience, registrations, fellowshipServiceTypes, setFieldValue } = this.props;
    const { experiences } = this.state;
    const selectedRegistration = find(propEq(['id'], values.registrationId), registrations);
    const selectedServiceType = find(propEq('id', values.fellowshipServiceTypeId), fellowshipServiceTypes);

    let hoursLeft = null;
    if (selectedRegistration && selectedServiceType) {
      const experiencesWithServiceType = filter(
        (e) => e.fellowshipServiceTypeId == selectedServiceType.id && e.id != experience.id,
        experiences,
      );
      const totalHours = sum(experiencesWithServiceType.map((e) => (e.approved || e.approved == null ? e.hours : 0)));
      const maxHours = selectedServiceType.percentage
        ? Math.round(selectedRegistration.requiredHours * (selectedServiceType.allowed * 0.01))
        : selectedServiceType.allowed;
      hoursLeft = maxHours - totalHours;
    }
    setFieldValue('hoursLeft', hoursLeft, true);
  };

  StaticEditField = (props) => {
    const { admin, experience } = this.props;
    const staticValue = defaultTo(props.value, props.static);

    return (experience.approved && !admin) || props.isStatic ? (
      !isNilOrEmpty(staticValue) && (
        <div className="form-group row">
          <label className="col-form-label col-md-3">{props.label}</label>
          <div className="col-md-7 d-flex align-items-center">{defaultTo(props.value, props.static)}</div>
        </div>
      )
    ) : (
      <EditField {...props} />
    );
  };

  render() {
    const {
      admin,
      approve,
      reject,
      experience,
      fellowshipServiceTypes,
      values,
      errors,
      handleBlur,
      handleChange,
      handleSubmit,
      isSubmitting,
      isValid,
      setValues,
      setFieldValue,
      status,
      registrations,
    } = this.props;
    const { StaticEditField } = this;
    const reviews = defaultTo([], experience.reviews);
    const firstName = path(['registration', 'user', 'firstName'], experience);
    const lastName = path(['registration', 'user', 'lastName'], experience);
    const certificationRegistrations = filter((r) => r.certificationId, registrations);

    // Determine whether or not this is a military spouse registration
    let isMilitary;
    if (admin || experience.approved) {
      isMilitary = path(['registration', 'militarySpouse'], experience);
    } else {
      isMilitary = compose(
        defaultTo(false),
        path(['militarySpouse']),
        find(propEq(['id'], values.registrationId)),
      )(certificationRegistrations);
    }

    return (
      <div>
        {admin && (
          <FlaggedUserModal
            user={experience.registration.user}
          >
          </FlaggedUserModal>
        )}
        <Card className="container">
          <Card.Header compact>Experience Hours Approval{isMilitary ? ' (Military Spouse)' : ''}</Card.Header>
          <Card.Body>
            <form className="offset-md-1">
              {firstName && (
                <StaticEditField
                  isStatic
                  label="Name"
                  static={
                    <Link to={routes.admin.users.profileBuilder(path(['registration', 'user', 'id'], experience))}>
                      {firstName} {lastName}
                    </Link>
                  }
                />
              )}
              <StaticEditField
                isStatic={admin}
                static={path(['registration', 'certification', 'name'], experience)}
                as={Select}
                bare
                simpleValue
                name="registrationId"
                label="Registration"
                onChange={(value) => setFieldValue('registrationId', value)}
                options={certificationRegistrations.map((registration) => ({
                  value: registration.id,
                  label: registration.certification.name,
                }))}
                value={values.registrationId}
              />
              <StaticEditField
                static={defaultTo(
                  '',
                  path(['name'], find(propEq('id', values.fellowshipServiceTypeId), fellowshipServiceTypes)),
                )}
                as={Select}
                bare
                simpleValue
                name="fellowshipServiceTypeId"
                label="Service Type"
                onChange={(value) => setFieldValue('fellowshipServiceTypeId', value)}
                options={fellowshipServiceTypes
                  .filter((type) => (!type.militarySpouseOnly || isMilitary) && isNilOrEmpty(type.deletedAt))
                  .map((service) => ({ value: service.id, label: service.name }))}
                value={values.fellowshipServiceTypeId}
                error={errors.fellowshipServiceTypeId}
              />
              {isMilitary && (
                <StaticEditField
                  type="number"
                  name="sessions"
                  label="How many Sessions"
                  required
                  status={status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.sessions}
                  error={errors.sessions}
                />
              )}
              {isMilitary && (
                <StaticEditField
                  type="number"
                  name="attendance"
                  label="Total people in attendance across ALL sessions"
                  tip="Unless direct service (one-on-one, group, or teaching), please enter 1"
                  required
                  status={status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.attendance}
                  error={errors.attendance}
                />
              )}
              {isMilitary && (
                <StaticEditField
                  name="practicumImpact"
                  label="What impact did you make in your practicum work this month?"
                  tip="FINRA Foundation strives to capture and share the important work you do. In support of their efforts, this field is included so you can share the story behind the numbers you report every month. Tell us a story – good or bad – about something meaningful that happened as you counseled or taught service members."
                  required
                  status={status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.practicumImpact}
                  error={errors.practicumImpact}
                />
              )}
              <StaticEditField
                name="organization"
                label="Organization"
                required
                status={status}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.organization}
                error={errors.organization}
              />
              {!isMilitary && (
                <StaticEditField
                  name="title"
                  label="Job Title"
                  required
                  status={status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.title}
                  error={errors.title}
                />
              )}
              <StaticEditField
                type="number"
                name="hours"
                label="Total Hours"
                required
                status={status}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.hours}
                error={errors.hours}
              />
              <StaticEditField
                name="supervisorName"
                label="Supervisor Name"
                tip={
                  isMilitary
                    ? 'If no supervisor, please list Sage Financial Solutions'
                    : 'List the person able to verify your participation and activity. If no supervisor, please upload supporting documentation.'
                }
                required
                status={status}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.supervisorName}
                error={errors.supervisorName}
              />
              <StaticEditField
                name="supervisorEmail"
                label="Supervisor Email"
                tip={
                  isMilitary
                    ? 'If no supervisor, please list militaryspouse@Sage Financial Solutions.org'
                    : 'If no supervisor, please list your email address.'
                }
                required
                status={status}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.supervisorEmail}
                error={errors.supervisorEmail}
              />
              <StaticEditField
                name="supervisorPhone"
                label="Supervisor Phone"
                tip="If no supervisor, please list yours."
                required
                status={status}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.supervisorPhone}
                error={errors.supervisorPhone}
              />
              <StaticEditField
                as="textarea"
                name="description"
                label="Description"
                required
                status={status}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.description}
                error={errors.description}
              />
              <StaticEditField
                name="startDate"
                label="Start Date"
                type="date"
                status={status}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.startDate}
                value={values.startDate}
              />
              <StaticEditField
                name="endDate"
                label="End Date"
                type="date"
                status={status}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.endDate}
                error={errors.endDate}
              />
              <div className="row form-group">
                <label className="col-form-label col-md-3">Supporting Documents</label>
                <div className="col-md-7">
                  {experience.approved ? (
                    values.media
                      .filter((file) => file.type == magic.supportingDocumentation)
                      .map((file) => <File key={file.id} file={file} />)
                  ) : (
                    <FileBrowser
                      files={values.media}
                      fileKey={magic.supportingDocumentation}
                      name="supportingDocumentationFile"
                      addFile={(file) => apiAddFile(experience, file)}
                      update={(media) => setFieldValue('media', media)}
                      fake={isNilOrEmpty(experience.id)}
                      fakeFiles={values.fakeMedia}
                      fakeUpdate={({ files, fakeFiles }) => setValues({ ...values, media: files, fakeMedia: fakeFiles })}
                    />
                  )}
                </div>
              </div>

              {reviews.length > 0 && (
                <div className="row">
                  <div className="col-md-10">
                    <h4 className="bold-blue-heading mt-5 mb-3">Reviews</h4>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Reviewer</th>
                          <th>Message</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reviews.map((review) => (
                          <tr key={review.id}>
                            <td>{moment(review.createdAt).format('MM/DD/YYYY')}</td>
                            <td>
                              {path(['user', 'firstName'], review)} {path(['user', 'lastName'], review)}
                            </td>
                            <td>{review.message}</td>
                            <td>
                              {review.approved ? (
                                <span className="badge badge-success">Approved</span>
                              ) : (
                                <span className="badge badge-danger">Rejected</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {(!experience.approved || admin) && (
                <div className="form-group row row mb-0">
                  <div className="col-md-10 text-right">
                    {reject && (
                      <button type="button" className="btn btn-outline-danger mr-2" onClick={reject}>
                        Reject
                      </button>
                    )}
                    {approve && (
                      <button type="button" className="btn btn-secondary mr-2" onClick={approve}>
                        Approve
                      </button>
                    )}
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={!isValid || isSubmitting}
                      onClick={handleSubmit}
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              )}
            </form>
          </Card.Body>
        </Card>
      </div>
    );
  }
}

export default compose(
  withRouter,
  withFormik({
    handleSubmit: (payload, formProps) => {
      const { props } = formProps;
      const { registrationId, ...rest } = payload;

      props
        .submit(registrationId, rest)
        .then((resp) => {
          handleSuccess(formProps);
          return resp;
        })
        .then((resp) => props.finish(resp))
        .catch((err) => handleError(formProps, err));
    },
    mapPropsToValues: ({ experience = {} }) => ({
      id: experience.id,
      registrationId: defaultTo('', experience.registrationId),
      fellowshipServiceTypeId: defaultTo('', experience.fellowshipServiceTypeId),
      sessions: defaultTo('', experience.sessions),
      attendance: defaultTo('', experience.attendance),
      practicumImpact: defaultTo('', experience.practicumImpact),
      organization: defaultTo('', experience.organization),
      title: defaultTo('', experience.title),
      hours: defaultTo('', experience.hours),
      supervisorName: defaultTo('', experience.supervisorName),
      supervisorEmail: defaultTo('', experience.supervisorEmail),
      supervisorPhone: defaultTo('', experience.supervisorPhone),
      description: defaultTo('', experience.description),
      startDate: defaultTo(moment().format('YYYY-MM-DD'), experience.startDate),
      endDate: defaultTo(moment().format('YYYY-MM-DD'), experience.endDate),
      media: defaultTo([], experience.media),
      fakeMedia: [],
      hoursLeft: null,
    }),
    validate: (values, props) => {
      const { registrations } = props;
      const errors = {};
      const selectedRegistration = defaultTo({}, find(propEq(['id'], values.registrationId), registrations));
      const selectedRegistrationEnrolledOn = defaultTo('', selectedRegistration.enrolledOn);
      const yearDifferential = moment(selectedRegistrationEnrolledOn).diff(values.startDate, 'years', true);

      if (selectedRegistration.militarySpouse) {
        if (isEmpty(values.sessions)) errors.sessions = 'Required';
        if (isEmpty(values.attendance)) errors.attendance = 'Required';
        if (isEmpty(values.practicumImpact)) errors.practicumImpact = 'Required';
      }
      if (isEmpty(values.fellowshipServiceTypeId)) errors.fellowshipServiceTypeId = 'Required';
      if (isEmpty(values.hours)) errors.hours = 'Required';
      if (isEmpty(values.supervisorName)) errors.supervisorName = 'Required';
      if (isEmpty(values.supervisorEmail)) errors.supervisorEmail = 'Required';
      if (isEmpty(values.supervisorPhone)) errors.supervisorPhone = 'Required';
      if (isEmpty(values.description)) errors.description = 'Required';
      if (yearDifferential > 2) {
        errors.startDate = 'You cannot log experience prior to 2 years of your registration date';
      }
      if (values.hoursLeft != null && values.hours > values.hoursLeft) {
        errors.hours = `You can only record ${values.hoursLeft} more hours`;
      }

      return errors;
    },
    isInitialValid: ({ initialValues, validate }) => isEmpty(validate(initialValues)),
    validateOnChange: true,
  }),
)(Experience);
